import { yupResolver } from "@hookform/resolvers/yup";
import { FormGroupUI } from "components/FormGroup";
import { FormGroupLabelUI } from "components/FormGroupLabel";
import { RhfSelect } from "components/rhfs/RhfSelect";
import { Button } from "components/ui/Button";
import { DEFAULT_SEARCH_FILTER } from "constants/search-fiilter";
import { type EmailSearchInput, RecruitmentType } from "hooks/useEmailsRestApi";
import { Minimize2 } from "lucide-react";
import { type FC, useCallback, useEffect } from "react";
import {
  FormProvider,
  type SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { HRTypeFormUI } from "routes/emails/SearchEmailsPage/components/HRTypeForm/HRTypeForm";
import { MatterTypeFormUI } from "routes/emails/SearchEmailsPage/components/MatterTypeForm/MatterTypeForm";
import * as yup from "yup";

const recruitmentTypeOptions = [
  { label: "案件", value: RecruitmentType.Matter },
  { label: "人材", value: RecruitmentType.HumanResource },
];

export type SearchEmailsFormUIProps = {
  isLoading: boolean;
  currentSearchInput: EmailSearchInput;
  isSearchFilterOpen: boolean;
  onSearch: SubmitHandler<EmailSearchInput>;
  onClearFilter: () => void;
  onClose: () => void;
};

export const SearchEmailsFormUI: FC<SearchEmailsFormUIProps> = ({
  isLoading,
  currentSearchInput,
  onSearch,
  onClearFilter,
  onClose,
}) => {
  const schema = yup
    .object()
    .shape({
      recruitmentType: yup.string().required(),
      hrFilter: yup.object().shape({
        genderType: yup.string().nullable(),
        ageRange: yup.object().shape({
          from: yup.number(),
          to: yup.number(),
        }),
        rewardRange: yup.object().shape({
          from: yup.number(),
          to: yup.number(),
        }),
        hrTypes: yup.object().shape({
          freerance: yup.boolean(),
          contractEmployee: yup.boolean(),
          fullTimeEmployee: yup.boolean(),
        }),
        immediately: yup.boolean(),
        needRemote: yup.boolean(),
        onlyJapanese: yup.boolean(),
      }),
      matterFilter: yup.object().shape({
        rewardRange: yup.object().shape({
          from: yup.number(),
          to: yup.number(),
        }),
        distanceFromEnd: yup.number(),
        oneOpportunity: yup.boolean(),
        otherCountriesEnabled: yup.boolean(),
        onlyEnableFreelance: yup.boolean(),
        enableRemote: yup.boolean(),
      }),
      limitedByDay: yup.number(),
      orderType: yup.string(),
      feewords: yup.array().of(yup.string()),
      skills: yup.array().of(yup.string()),
      favoritesOnly: yup.boolean(),
    })
    .required();

  const methods = useForm<EmailSearchInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...DEFAULT_SEARCH_FILTER,
      ...currentSearchInput,
      recruitmentType:
        currentSearchInput.recruitmentType ?? RecruitmentType.Matter,
    },
  });

  const recruitmentType = useWatch({
    control: methods.control,
    name: "recruitmentType",
    defaultValue: currentSearchInput.recruitmentType ?? RecruitmentType.Matter,
  });

  const isMatter = recruitmentType === RecruitmentType.Matter;

  useEffect(() => {
    methods.reset({
      ...DEFAULT_SEARCH_FILTER,
      ...currentSearchInput,
      recruitmentType:
        currentSearchInput.recruitmentType ?? RecruitmentType.Matter,
    });
  }, [currentSearchInput, methods]);

  // 検索対象（案件/人材）切り替え時に、フィルターをクリアする
  const handleRecruitmentTypeChange = useCallback(
    (value: string) => {
      methods.reset({
        ...DEFAULT_SEARCH_FILTER,
        recruitmentType: value as RecruitmentType,
        freeWords: [],
        skills: [],
      });
    },
    [methods],
  );

  return (
    <div className="text-base-content w-full h-full">
      <div className="flex flex-col h-full">
        <div className="bg-white flex flex-col gap-3 rounded-lg border border-slate-200 shadow-sm p-4 relative h-full">
          <button
            type="button"
            onClick={onClose}
            aria-label="検索フィルターを縮小"
            className="absolute top-1 right-1 p-1 rounded hover:bg-base-200 text-base-content/60 hover:text-base-content"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <FormProvider {...methods}>
            <form
              onSubmit={methods.handleSubmit(onSearch)}
              className="flex flex-col flex-1 min-h-0"
            >
              <div className="flex-1 min-h-0 overflow-y-auto">
                <FormGroupUI className="mb-6">
                  <FormGroupLabelUI>検索対象</FormGroupLabelUI>
                  <RhfSelect
                    name="recruitmentType"
                    width="narrow"
                    options={recruitmentTypeOptions}
                    onValueChange={handleRecruitmentTypeChange}
                  />
                </FormGroupUI>
                {isMatter ? <MatterTypeFormUI /> : <HRTypeFormUI />}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5 pt-3 border-t border-slate-200">
                <Button
                  data-testid="search-btn"
                  type="submit"
                  size="md"
                  width="full"
                  disabled={isLoading}
                >
                  検索
                </Button>
                <Button
                  type="button"
                  isOutline
                  size="md"
                  width="full"
                  disabled={isLoading}
                  onClick={() => {
                    onClearFilter();
                  }}
                >
                  クリア
                </Button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};
